import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  LocalOffer as LocalOfferIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { MarketingProductVariant, TargetPriceItem, PromotionTarget, TargetType } from '../../types/marketingTypes';

interface TargetPriceManagerProps {
  selectedVariants: MarketingProductVariant[];
  onTargetPricesChange: (targetPrices: TargetPriceItem[]) => void;
  initialTargetPrices?: TargetPriceItem[];
  showOriginalPrice?: boolean;
}

const TargetPriceManager: React.FC<TargetPriceManagerProps> = ({
  selectedVariants,
  onTargetPricesChange,
  initialTargetPrices = [],
  showOriginalPrice = true
}) => {
  const [targetPrices, setTargetPrices] = useState<{ [variantId: string]: number }>({});
  const [errors, setErrors] = useState<{ [variantId: string]: string }>({});

  // تحميل الأسعار المستهدفة الأولية
  useEffect(() => {
    const initialPrices: { [variantId: string]: number } = {};
    
    // تحميل من initialTargetPrices إذا كانت متوفرة
    initialTargetPrices.forEach(item => {
      if (item.target.targetType === TargetType.PRODUCT_VARIANT) {
        initialPrices[item.target.targetId] = item.targetPrice;
      }
    });
    
    // تحميل من targetPrice في المتغيرات إذا كانت متوفرة
    selectedVariants.forEach(variant => {
      if (variant.targetPrice && !initialPrices[variant._id]) {
        initialPrices[variant._id] = variant.targetPrice;
      }
    });
    
    setTargetPrices(initialPrices);
    
    // تحديث الأسعار المستهدفة بعد التحميل
    setTimeout(() => updateTargetPrices(initialPrices), 100);
  }, [initialTargetPrices.length, selectedVariants.length]); // تغيير التبعيات لتجنب الحلقة اللا نهائية

  // دالة لتحديث الأسعار المستهدفة
  const updateTargetPrices = (currentPrices = targetPrices) => {
    const targetPriceItems: TargetPriceItem[] = selectedVariants
      .filter(variant => currentPrices[variant._id] && currentPrices[variant._id] > 0)
      .map(variant => ({
        target: {
          targetType: TargetType.PRODUCT_VARIANT,
          targetId: variant._id,
          quantity: 1
        },
        targetPrice: currentPrices[variant._id]
      }));
    
    onTargetPricesChange(targetPriceItems);
  };

  const handlePriceChange = (variantId: string, value: string) => {
    const numericValue = parseFloat(value);
    const newTargetPrices = { ...targetPrices };
    const newErrors = { ...errors };

    if (value === '' || value === '0') {
      delete newTargetPrices[variantId];
      delete newErrors[variantId];
    } else if (isNaN(numericValue) || numericValue < 0) {
      newErrors[variantId] = 'يجب أن يكون السعر رقماً موجباً';
    } else {
      newTargetPrices[variantId] = numericValue;
      delete newErrors[variantId];
    }

    setTargetPrices(newTargetPrices);
    setErrors(newErrors);
    
    // تحديث الأسعار المستهدفة فوراً
    setTimeout(() => updateTargetPrices(newTargetPrices), 0);
  };

  const removeTargetPrice = (variantId: string) => {
    const newTargetPrices = { ...targetPrices };
    const newErrors = { ...errors };
    delete newTargetPrices[variantId];
    delete newErrors[variantId];
    setTargetPrices(newTargetPrices);
    setErrors(newErrors);
    
    // تحديث الأسعار المستهدفة فوراً
    setTimeout(() => updateTargetPrices(newTargetPrices), 0);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2
    }).format(price);
  };

  const getVariantDisplayName = (variant: MarketingProductVariant) => {
    const attributesText = variant.attributes && variant.attributes.length > 0
      ? variant.attributes.map(attr => `${attr.name}: ${attr.value}`).join(', ')
      : 'لا توجد خصائص';
    
    return `${variant.productName || variant.sku || 'غير محدد'} - ${attributesText}`;
  };

  if (selectedVariants.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          border: '1px dashed',
          borderColor: 'grey.300',
          borderRadius: 2
        }}
      >
        <LocalOfferIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          لم يتم اختيار أي منتجات بعد
        </Typography>
        <Typography variant="body2" color="text.secondary">
          اختر المنتجات أولاً لتتمكن من تحديد الأسعار المستهدفة
        </Typography>
      </Paper>
    );
  }

  const variantsWithTargetPrices = selectedVariants.filter(variant => targetPrices[variant._id]);
  const variantsWithoutTargetPrices = selectedVariants.filter(variant => !targetPrices[variant._id]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }} align="right">
          تحديد الأسعار المستهدفة
        </Typography>
        <Chip
          label={`${selectedVariants.length} منتج`}
          color="primary"
          variant="outlined"
          size="medium"
        />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon />
          <Typography variant="body2">
            حدد السعر المستهدف لكل منتج. المنتجات التي لا تحتوي على سعر مستهدف لن تكون مشمولة في الحملة.
          </Typography>
        </Box>
      </Alert>

      {/* المنتجات التي تحتوي على أسعار مستهدفة */}
      {variantsWithTargetPrices.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
            المنتجات المشمولة في الحملة ({variantsWithTargetPrices.length})
          </Typography>
          <Grid container spacing={2}>
            {variantsWithTargetPrices.map((variant) => (
              <Grid item xs={12} md={6} key={variant._id}>
                <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.200' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <LocalOfferIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            mb: 1
                          }}
                        >
                          {getVariantDisplayName(variant)}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          الباركود: {variant.barcode || 'غير محدد'}
                        </Typography>

                        {showOriginalPrice && variant.price > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            السعر الأصلي: {formatPrice(variant.price)}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            label="السعر المستهدف"
                            type="number"
                            size="small"
                            value={targetPrices[variant._id] || ''}
                            onChange={(e) => handlePriceChange(variant._id, e.target.value)}
                            error={!!errors[variant._id]}
                            helperText={errors[variant._id]}
                            InputProps={{
                              inputProps: { min: 0, step: 0.01 },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon fontSize="small" />
                                </InputAdornment>
                              )
                            }}
                            sx={{ flexGrow: 1 }}
                          />
                          <Tooltip title="إزالة السعر المستهدف">
                            <IconButton
                              onClick={() => removeTargetPrice(variant._id)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* المنتجات التي لا تحتوي على أسعار مستهدفة */}
      {variantsWithoutTargetPrices.length > 0 && (
        <Box>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
            المنتجات غير المشمولة في الحملة ({variantsWithoutTargetPrices.length})
          </Typography>
          <Grid container spacing={2}>
            {variantsWithoutTargetPrices.map((variant) => (
              <Grid item xs={12} md={6} key={variant._id}>
                <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'grey.400', width: 40, height: 40 }}>
                        <LocalOfferIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            mb: 1
                          }}
                        >
                          {getVariantDisplayName(variant)}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          الباركود: {variant.barcode || 'غير محدد'}
                        </Typography>

                        {showOriginalPrice && variant.price > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            السعر الأصلي: {formatPrice(variant.price)}
                          </Typography>
                        )}

                        <TextField
                          label="السعر المستهدف"
                          type="number"
                          size="small"
                          value={targetPrices[variant._id] || ''}
                          onChange={(e) => handlePriceChange(variant._id, e.target.value)}
                          error={!!errors[variant._id]}
                          helperText={errors[variant._id] || 'أدخل السعر المستهدف لإضافة هذا المنتج للحملة'}
                          InputProps={{
                            inputProps: { min: 0, step: 0.01 },
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                          fullWidth
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default TargetPriceManager;